import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  TablePagination,
  Tooltip,
  Skeleton,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CompareIcon from '@mui/icons-material/Compare';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import lifeDeclarationReviewService from '../services/lifeDeclarationReviewService';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const tableHeadCellSx = {
  backgroundColor: '#4E83BD',
  color: 'white',
  fontWeight: 700,
  fontSize: '13px',
  py: 1.5,
  whiteSpace: 'nowrap',
};

const scoreChipColor = (score) => {
  const n = parseFloat(score);
  if (isNaN(n)) return { bg: '#F1F5F9', color: '#64748B' };
  if (n >= 80) return { bg: '#DCFCE7', color: '#166534' };
  if (n >= 65) return { bg: '#FEF9C3', color: '#854D0E' };
  return { bg: '#FEE2E2', color: '#991B1B' };
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

const LifeDeclarationReview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ──────────────────────────────────────────────
  // Fetch list
  // ──────────────────────────────────────────────

  const fetchReviews = useCallback(
    async (pageOverride) => {
      setListLoading(true);
      setListError(null);
      const params = { page: (pageOverride ?? page) + 1, page_size: rowsPerPage };
      const result = await lifeDeclarationReviewService.listReviews(params);
      if (result.success) {
        setReviews(result.data.results || []);
        setTotalCount(result.data.count || 0);
      } else {
        setListError(result.error);
        setReviews([]);
      }
      setListLoading(false);
    },
    [page, rowsPerPage]
  );

  useEffect(() => {
    fetchReviews();
  }, [page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ──────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const filteredReviews = searchQuery.trim()
    ? reviews.filter(
        (r) =>
          r.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.declaration_id?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : reviews;

  const renderScoreChip = (score) => {
    const { bg, color } = scoreChipColor(score);
    return (
      <Chip
        label={`${parseFloat(score || 0).toFixed(2)}%`}
        size="small"
        sx={{ backgroundColor: bg, color, fontWeight: 700, fontSize: '12px' }}
      />
    );
  };

  const renderStatusChip = (status) => (
    <Chip
      label={status?.replace(/_/g, ' ') || '—'}
      size="small"
      sx={{
        backgroundColor: '#FEF9C3',
        color: '#854D0E',
        fontWeight: 600,
        fontSize: '11px',
        textTransform: 'capitalize',
      }}
    />
  );

  // ──────────────────────────────────────────────
  // JSX
  // ──────────────────────────────────────────────

  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>

      {/* ── Page Header ───────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          mb: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <CompareIcon sx={{ color: 'white', fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, fontSize: { xs: '16px', sm: '20px' }, color: 'white', lineHeight: 1.2 }}
            >
              Life Declaration Review
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', mt: 0.25 }}>
              Manually verify borderline face-verification submissions.
            </Typography>
          </Box>
          <Chip
            label={`${totalCount} Pending`}
            size="small"
            sx={{ backgroundColor: '#FEF9C3', color: '#854D0E', fontWeight: 700, fontSize: '12px' }}
          />
        </Box>
      </Paper>

      {/* ── Toolbar ───────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by name or email…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: 'white',
              '& fieldset': { borderColor: '#E2E8F0' },
              '&:hover fieldset': { borderColor: '#4E83BD' },
              '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
            },
          }}
        />
        <Tooltip title="Refresh">
          <IconButton
            onClick={() => { setPage(0); fetchReviews(0); }}
            sx={{ backgroundColor: 'white', border: '1px solid #E2E8F0', borderRadius: '8px' }}
          >
            <RefreshIcon sx={{ color: '#4E83BD' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Error Banner ──────────────────────────── */}
      {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

      {/* ── Table ─────────────────────────────────── */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', overflow: 'hidden' }}>

        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            backgroundColor: '#F8FAFC',
            borderBottom: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <RateReviewIcon sx={{ color: '#4E83BD', fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1E293B' }}>
            Pending Reviews
          </Typography>
        </Box>

        {listLoading ? (
          <Box sx={{ p: 3 }}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: 1, mb: 1 }} />
            ))}
          </Box>
        ) : filteredReviews.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CompareIcon sx={{ color: '#CBD5E1', fontSize: 48, mb: 1 }} />
            <Typography sx={{ color: '#94A3B8', fontSize: '14px' }}>
              No declarations pending review
            </Typography>
          </Box>
        ) : (
          <>
            {!isMobile ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>User</TableCell>
                      <TableCell sx={tableHeadCellSx}>Comparison Score</TableCell>
                      <TableCell sx={tableHeadCellSx}>Method</TableCell>
                      <TableCell sx={tableHeadCellSx}>Status</TableCell>
                      <TableCell sx={tableHeadCellSx}>Submitted At</TableCell>
                      <TableCell sx={tableHeadCellSx}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReviews.map((row) => (
                      <TableRow
                        key={row.declaration_id}
                        sx={{ backgroundColor: 'white', '&:hover': { backgroundColor: '#EFF6FF' } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                backgroundColor: '#DBEAFE',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <PersonIcon sx={{ color: '#3B82F6', fontSize: 18 }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#1E293B' }}>
                                {row.user_name || '—'}
                              </Typography>
                              <Typography sx={{ fontSize: '11px', color: '#64748B' }}>
                                {row.user_email || '—'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{renderScoreChip(row.comparison_score)}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '12px', color: '#475569', textTransform: 'capitalize' }}>
                            {row.submission_method?.replace(/_/g, ' ') || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{renderStatusChip(row.review_status)}</TableCell>
                        <TableCell sx={{ fontSize: '12px', color: '#475569' }}>
                          {row.declaration_timestamp
                            ? new Date(row.declaration_timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CompareIcon />}
                            onClick={() => navigate(`/life-declaration-review/${row.declaration_id}`)}
                            sx={{
                              textTransform: 'none',
                              fontSize: '12px',
                              fontWeight: 600,
                              borderRadius: '8px',
                              background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                              boxShadow: '0 2px 6px rgba(78,131,189,0.3)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #3A6A99 0%, #2D5580 100%)',
                              },
                            }}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {filteredReviews.map((row) => (
                  <Card key={row.declaration_id} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1E293B' }}>
                            {row.user_name || '—'}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#64748B' }}>{row.user_email}</Typography>
                        </Box>
                        {renderScoreChip(row.comparison_score)}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                        {renderStatusChip(row.review_status)}
                        <Typography sx={{ fontSize: '11px', color: '#64748B', alignSelf: 'center' }}>
                          {row.declaration_timestamp
                            ? new Date(row.declaration_timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                            : ''}
                        </Typography>
                      </Box>
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        startIcon={<CompareIcon />}
                        onClick={() => navigate(`/life-declaration-review/${row.declaration_id}`)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                        }}
                      >
                        Review Images
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: '1px solid #E2E8F0' }}
            />
          </>
        )}
      </Paper>

      {/* ── Snackbar ──────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LifeDeclarationReview;
